import { DownloadOutlined, ShareAltOutlined } from '@ant-design/icons';
import { Button, Modal } from 'antd';
import cn from 'classnames';
import DOMPurify from 'dompurify';
import { useMemo, useRef, useState } from 'react';

import { useLazyDownloadQuery } from '@app/api/ClientFiles';
import {
  LEAD_MSG_STATUS_DELETED,
  formatLeadMessageText,
  formatLeadMessageTimestamp,
  getLeadMessageStatusColor,
  getLeadMessageStatusIcon,
} from '@app/components/LeadCard/ui/MessageHistory/lib';
import { config } from '@app/config';
import { useUserPrint } from '@app/hooks';
import type { NFiles } from '@app/types';

import type { FC } from 'react';

import './LeadVideoAttachment.scss';

const getVideoMimeByExt = (ext: string): string => {
  switch (ext) {
    case 'mp4':
      return 'video/mp4';
    default:
      return 'video/webm';
  }
};

interface LeadVideoAttachmentProps {
  file: NFiles.ModelClientFile;
  message: string | null;
  socialType: string | null;
  isIncoming: boolean;
  timestamp: string;
  wasEdited: boolean;
  status: string | null;
  isForwarded: boolean;
  replyMessage: string | null;
  replyMessageId: number | null;
  showStatus?: boolean;
}

export const LeadVideoAttachment: FC<LeadVideoAttachmentProps> = ({
  file,
  message,
  socialType,
  isIncoming,
  timestamp,
  wasEdited,
  status,
  isForwarded,
  replyMessage,
  replyMessageId,
  showStatus = true,
}) => {
  const { params } = useUserPrint();
  const [downloadFile] = useLazyDownloadQuery();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const downloadAnchorRef = useRef<HTMLAnchorElement>(null);

  const ext = (file.Extension ?? '').trim().toLowerCase();
  const mime = getVideoMimeByExt(ext);

  const streamUrl = useMemo(
    () =>
      `${config.backendHosts.restHost}/api/ClientFiles/Stream/${params.Database}/${file.ClientId}/${file.ExternalAccessId}`,
    [params.Database, file.ClientId, file.ExternalAccessId]
  );

  const isDeleted = status === LEAD_MSG_STATUS_DELETED;
  const showStatusIcon = !isIncoming || isDeleted;
  const statusIcon = showStatusIcon ? getLeadMessageStatusIcon(status) : null;
  const statusColor = getLeadMessageStatusColor(status);
  const formattedTimestamp = formatLeadMessageTimestamp(timestamp);

  const sanitizedReplyHtml = replyMessage
    ? DOMPurify.sanitize(formatLeadMessageText(replyMessage, socialType))
    : null;
  const sanitizedMessageHtml = message
    ? DOMPurify.sanitize(formatLeadMessageText(message, socialType))
    : null;

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDownloading(true);

    try {
      const buffer = await downloadFile({
        clientId: file.ClientId,
        fileId: file.Id,
        ActiveBranchId: params.ActiveBranchId,
      }).unwrap();

      const blob = new Blob([buffer], { type: mime });
      const url = URL.createObjectURL(blob);

      if (downloadAnchorRef.current) {
        downloadAnchorRef.current.href = url;
        downloadAnchorRef.current.download =
          file.FileName ?? `video.${ext || 'mp4'}`;
        downloadAnchorRef.current.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      // silent
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePreviewOpen = () => {
    setIsPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setIsPreviewOpen(false);
  };

  return (
    <div
      className={cn('LeadVideoAttachment', {
        'LeadVideoAttachment--out': !isIncoming,
        'LeadVideoAttachment--deleted': isDeleted,
      })}
    >
      {isForwarded && (
        <div className="LeadVideoAttachment__forwarded">
          <ShareAltOutlined className="LeadVideoAttachment__forwarded-icon" />
          Пересланное сообщение
        </div>
      )}
      {sanitizedReplyHtml && (
        <div
          className="LeadVideoAttachment__reply"
          data-reply-target-id={replyMessageId ?? undefined}
        >
          <div className="LeadVideoAttachment__reply-indicator" />
          <div
            className="LeadVideoAttachment__reply-text"
            dangerouslySetInnerHTML={{ __html: sanitizedReplyHtml }}
          />
        </div>
      )}
      <div className="LeadVideoAttachment__player-area">
        <video
          controls
          src={streamUrl}
          className="LeadVideoAttachment__video"
          onClick={handlePreviewOpen}
        />
        <Button
          className="LeadVideoAttachment__download-btn"
          icon={<DownloadOutlined />}
          size="small"
          loading={isDownloading}
          onClick={handleDownload}
        />
      </div>
      <a
        ref={downloadAnchorRef}
        className="LeadVideoAttachment__download-anchor"
      />
      <Modal
        centered
        className="LeadVideoAttachment__modal"
        destroyOnHidden
        footer={null}
        open={isPreviewOpen}
        title={file.FileName ?? 'Видео'}
        width="min(920px, 92vw)"
        onCancel={handlePreviewClose}
      >
        <video
          autoPlay
          controls
          src={streamUrl}
          className="LeadVideoAttachment__modal-video"
        />
      </Modal>
      {sanitizedMessageHtml && (
        <div
          className="LeadVideoAttachment__text"
          dangerouslySetInnerHTML={{ __html: sanitizedMessageHtml }}
        />
      )}
      {showStatus && (
        <div className="LeadVideoAttachment__status">
          <span className="LeadVideoAttachment__timestamp">
            {wasEdited && 'изменено '}
            {formattedTimestamp}
          </span>
          {statusIcon && (
            <span
              className="LeadVideoAttachment__status-icon"
              style={{ color: statusColor }}
            >
              {statusIcon}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
